import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { useAuth } from '../AuthContext'; 
import './SupervisorDashboard.css';
import SignOffStatus from './SignOffStatus';

const SupervisorDashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        console.log(`Using endpoint /api/incidents/supervisor-tasks for user ${user?.username || user?.email || 'undefined'}`);
        
        // Fallback to localStorage if user is not populated
        const userEmail = user?.username || user?.email || localStorage.getItem('userEmail') || 'pgd.hundal@gmail.com';

        const response = await API.get('/api/incidents/supervisor-tasks', {
          params: {
            username: userEmail, // Pass the email/username to the backend
          },
        });

        console.log('Supervisor tasks data:', response.data);
        setTasks(response.data.tasks || []);
      } catch (error) {
        console.error('Error fetching supervisor tasks:', error);
        setError('Failed to load tasks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchTasks();
    }
  }, [isAuthenticated, user]);
  
  const navigateToIncidentReview = (incidentId) => {
    navigate(`/incidents/${incidentId}/review`);
  };

  const isOverdue = (date) => {
    const now = new Date();
    const taskDate = new Date(date);
    const diffInHours = (now - taskDate) / (1000 * 60 * 60); // Calculate difference in hours
    return diffInHours > 72; // Overdue if more than 72 hours
  };

  const handleSignOff = async (incidentId) => {
    try {
      const response = await API.post(`/api/signoffs/investigation/${incidentId}/SUPERVISOR`, {
        notes: 'Supervisor approved the findings.',
      });
      if (response.data.success) {
        alert('Sign-off recorded successfully.');
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.ID === incidentId ? { ...task, INVESTIGATION_STATUS: 'SUPERVISOR_APPROVED' } : task
          )
        );
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error recording sign-off:', error);
      alert(`Failed to record sign-off: ${error.message}`);
    }
  };

  const handleRejection = async (incidentId) => {
    const rejectionNotes = prompt('Please provide rejection notes:');
    if (!rejectionNotes) return;

    try {
      const response = await API.post(`/api/signoffs/investigation/${incidentId}/SUPERVISOR`, {
        notes: rejectionNotes,
        status: 'REJECTED',
      });
      if (response.data.success) {
        alert('Rejection recorded successfully.');
        setTasks((prevTasks) =>
          prevTasks.map((task) =>
            task.ID === incidentId ? { ...task, INVESTIGATION_STATUS: 'REJECTED' } : task
          )
        );
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      console.error('Error recording rejection:', error);
      alert(`Failed to record rejection: ${error.message}`);
    }
  };

  const renderIncidentDetails = (incident) => {
    return (
      <div className="incident-details-panel">
        <h3>{incident.TITLE}</h3>
        
        {/* Other incident details */}
      
        
        <div className="actions-panel">
          <button onClick={() => handleSignOff(incident.ID)}>Approve</button>
          <button onClick={() => handleRejection(incident.ID)}>Reject</button>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return <div className="loading">Loading tasks...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  // Group tasks by status
  const submittedTasks = tasks.filter(task => task.INVESTIGATION_STATUS === 'SUBMITTED');
  const openTasks = tasks.filter(task => task.INVESTIGATION_STATUS === 'OPEN');
  const completedTasks = tasks.filter(task => task.INVESTIGATION_STATUS === 'COMPLETED');
  
  return (
    <div className="supervisor-dashboard">
      <h2>Supervisor Dashboard</h2>
      
      {/* Testing mode banner */}
      {user && user.USERNAME && (user.USERNAME.toLowerCase() === 'pgd.hundal@gmail.com' || user.USERNAME.toLowerCase() === 'john@smith.com') && (
        <div className="test-mode-banner">
          <strong>Testing Mode:</strong> You are viewing all departments as a test supervisor.
        </div>
      )}
      
      <div className="task-counts">
        <div className="count-badge needs-review">
          <span className="count">{submittedTasks.length}</span>
          <span className="label">Need Review</span>
        </div>
        <div className="count-badge open">
          <span className="count">{openTasks.length}</span>
          <span className="label">Open</span>
        </div>
        <div className="count-badge completed">
          <span className="count">{completedTasks.length}</span>
          <span className="label">Completed</span>
        </div>
      </div>
      
      {submittedTasks.length > 0 && (
        <div className="tasks-section">
          <h3>Submissions Needing Review</h3>
          <div className="tasks-container">  {/* Add this wrapper */}
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Department</th>
                  <th>Delegate</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submittedTasks.map((task) => (
                  <tr
                    key={task.ID}
                    className={isOverdue(task.DATE) ? 'overdue-task' : ''}
                    onClick={() => navigateToIncidentReview(task.ID)}
                  >
                    <td>#{task.ID}</td>
                    <td className="title-cell">{task.TITLE}</td>
                    <td>{new Date(task.DATE).toLocaleDateString()}</td>
                    <td>{task.PRIMARY_DEPARTMENT}</td>
                    <td>{task.DELEGATE_EMAIL}</td>
                    <td>
                      <Link to={`/incidents/${task.ID}/review`} className="review-button">
                        Review
                      </Link>
                      <button
                        className="sign-off-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleSignOff(task.ID);
                        }}
                      >
                        Sign Off
                      </button>
                      <button
                        className="reject-button"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleRejection(task.ID);
                        }}
                      >
                        Reject
                      </button>
                      <SignOffStatus task={task} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Similar sections for open and completed tasks if needed */}
    </div>
  );
};

export default SupervisorDashboard;